import {
  pageVisibleOnce,
  withPageLoadingRecovery,
} from '@forest/native-services'
import type { JSX } from 'react'
import { loadSelectedFeaturePage } from '~/lib/feature-page'
import { closeAfterStartupFailure } from './terminal-close'

/**
 * Daily content loading, recovered inside the presentation's original budget.
 *
 * The content load is a repeatable read of the selected feature's page and its
 * assets, so a failed attempt is re-entered while the original loading budget
 * can still fit another one. Recovery stays inside this one operation: the
 * document is never replaced, the presentation and the selected feature are
 * kept, and no intermediate failure reaches the consumer. When no safe recovery
 * remains, the page's existing terminal close runs once and the rejection still
 * propagates, so the cover is never removed over content that never loaded.
 */
export function loadContentWithRecovery(): Promise<{
  default: () => JSX.Element
}> {
  return withPageLoadingRecovery(loadContent).catch((error: unknown) => {
    closeAfterStartupFailure()
    throw error
  })
}

/**
 * One content load attempt, held until the presentation has spoken.
 *
 * The content load starts with the document, which can be before the native
 * presentation notifies the page of its remaining loading budget. A failure
 * that lands in that window must not be read as "this presentation has no
 * budget to recover in", so it waits for the notification and only then lets
 * the shared recovery decide. The wait is bounded: a presentation that never
 * announces itself must not hold a failed load unsettled for the life of the
 * page. A healthy load never reaches this path, and once the notification has
 * arrived the wait is already over.
 */
async function loadContent(): Promise<{ default: () => JSX.Element }> {
  try {
    return await loadSelectedFeaturePage()
  } catch (error) {
    await pageVisibleOnce.waitAtMost()
    throw error
  }
}
